import { Refresh } from 'iconsax-react';
import type { PropsWithChildren, ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';
import AnimatedLayout from '@/components/layouts/page-layout/animated-layout';
import { usePullToRefresh } from '@/hooks/use-pull-to-refresh';
import { useRootLayoutContext } from '@/routes/root.context';
import cn from '@/utils/merge-class-names';
import AppHeader, { type Props as AppHeaderProps } from './app-header';

type HeaderConfig = Omit<AppHeaderProps, 'children'> & {
  headerLabel?: ReactNode;
  headerClassname?: string;
  classname?: string;
};

export type PageLayoutProps = {
  title?: ReactNode;
  className?: string;
  headerConfig?: HeaderConfig;
  enablePullToRefresh?: boolean;
  constrainHeight?: boolean; // Constraint the height of the page layout to the screen height, used when we need the scroll button to show properly
  bannerColor?: 'light' | 'dark';
};

export default function PageLayout({
  children,
  title,
  className,
  headerConfig,
  constrainHeight,
  enablePullToRefresh = true,
  bannerColor = 'light',
}: PropsWithChildren<PageLayoutProps>) {
  const { showFooter } = useRootLayoutContext();
  const { isRefreshing, pullPosition } = usePullToRefresh(enablePullToRefresh);

  return (
    <>
      <Helmet>
        {title && <title>{title}</title>}
        {bannerColor && (
          <meta
            name='theme-color'
            content={bannerColor === 'light' ? '#fff' : '#000'}
          />
        )}
      </Helmet>
      <AnimatedLayout
        className={cn(
          'flex flex-1 flex-col items-stretch origin-center pt-safe bg-neutral-0 max-w-100dvw group-[.modal-layout]:w-full',
          {
            'max-h-dvh overflow-y-auto': constrainHeight,
          },
          showFooter
            ? 'pb-safe-offset-16 desktop:pb-safe-or-0 mode-embedded:pb-0 mode-embedded:pt-safe-offset-14 layout-compact:pt-safe-offset-10 layout-mini:pt-safe-offset-10'
            : 'mode-embedded:pt-0',
          className,
        )}>
        {headerConfig && (
          <AppHeader
            {...headerConfig}
            className={cn(
              'mode-embedded:bg-primary mode-embedded:layout-full:text-white mode-embedded:my-0 layout-mini:bg-neutral-0 layout-compact:bg-neutral-0 layout-compact:text-base-content',
              headerConfig.className,
            )}>
            {headerConfig.headerLabel === undefined || typeof headerConfig.headerLabel === 'string' ? (
              <h3
                className={cn(
                  'pointer-events-none flex-1 text-left text-2xl font-bold mode-embedded:text-center mode-embedded:text-[1rem]',
                  headerConfig.headerClassname,
                )}>
                {headerConfig.headerLabel ?? title}
              </h3>
            ) : (
              headerConfig.headerLabel
            )}
          </AppHeader>
        )}

        {children}

        <div
          style={{
            top: (isRefreshing ? 180 : pullPosition) / 3,
            opacity: isRefreshing || pullPosition > 0 ? 1 : 0,
          }}
          className='pointer-events-none fixed inset-x-1/2 z-[999] size-9 -translate-x-1/2 rounded-full bg-base-100 p-1 shadow text-primary'>
          <Refresh
            className={cn('size-full', { 'animate-spin': isRefreshing })}
            style={!isRefreshing ? { transform: `rotate(${pullPosition}deg)` } : {}}
          />
        </div>
      </AnimatedLayout>
    </>
  );
}
